import type { Ref } from "vue";
import { computed, reactive, readonly, ref, watch } from "vue";
import { defineStore } from "pinia";
import { PaymentScheduleType, scheduledPaymentFormDefaultState, scheduledPaymentFormValidations } from "@/types/scheduledPaymentsTypes";
import { minLength, required } from "@vuelidate/validators";
import {
  nameValidator,
  payIdIdentifierValidator,
  queriedBpayBillerValidator,
  queriedPayIdValidator,
  verifiedBpayBillerValidator,
  verifiedBsbValidator,
  verifiedPayIdIdentifierValidator,
} from "@/utils/validationRules";
import { useVuelidate } from "@vuelidate/core";
import * as Sentry from "@sentry/vue";
import { createAccountPayee, createBillerPayee, createPayIdPayee, PayeeType } from "@/api/addressBookApi";
import type { Recipient, ScheduleOptions } from "@/api/scheduledPaymentApi";
import { createScheduledPayment } from "@/api/scheduledPaymentApi";
import { makeBankTransferPayment, makeBPayPayment, makePayIdPayment, PaymentType } from "@/api/paymentApi";
import { routeNames } from "@/router/constants";
import { accountNumberValidator, amountValidator, bsbValidator, displayCurrency, useIntl, useToast } from "@moroku/money-webapp-components";
import { PAYMENT_STATE, type PaymentForm } from "@/types/payments";
import {
  AccountType,
  BPayBillerFragmentFragmentDoc,
  LookupBpayBillerDocument,
  LookupPayIdDocument,
  PayIdDetailsFragmentFragmentDoc,
  PayIdType,
  PaymentEndCondition,
  PaymentFrequency,
  RecipientType,
} from "@/api/generated/graphql";
import { useApolloClient } from "@vue/apollo-composable";
import { useFragment } from "@/api/generated";
import { useRouter } from "vue-router";
import { useAccounts } from "@/composables/useAccounts.ts";
import { isEmpty } from "radash";
import { makeTransfer } from "@/api/transferApi.ts";

export const usePaymentsStore = defineStore("payments", () => {
  const toast = useToast();
  const router = useRouter();
  const { t } = useIntl();

  const error = ref();
  const isSubmitting = ref(false);
  const {
    accounts,
    loading: loadingAccounts,
    refetch: refetchAccounts,
  } = useAccounts({
    includeExternal: false,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const accountsForSelect = computed(() => accounts.value);

  const generateInitialStoreForm = (): PaymentForm => ({
    details: {
      paymentType: PaymentType.BankTransfer,
      fromAccount: null,
      name: "",
      amount: null,
      description: "",
      savePayeeToAddressBook: false,
      scheduledPayment: scheduledPaymentFormDefaultState(),
      state: PAYMENT_STATE.NEW,
    },
    transfer: {
      toAccount: null,
    },
    bankTransfer: {
      accountNumber: "",
      bsb: "",
    },
    bpay: {
      billerCode: "",
      crn: "",
      verifiedBillerCode: "",
      lastQueriedBillerCode: "",
    },
    payId: {
      identifier: "",
      type: PayIdType.Email,
      verifiedPayIdIdentifier: "",
      lastQueriedIdentifier: "",
    },
    dateTime: null,
  });

  const formState: PaymentForm = reactive(generateInitialStoreForm());

  const fromAccountName = computed(() =>
    formState?.details?.fromAccount
      ? `${formState.details.fromAccount.name} — ${displayCurrency(formState.details.fromAccount.currentBalance)}`
      : null,
  );

  const validationRules = {
    details: {
      fromAccount: { required },
      name: { nameValidator: nameValidator() },
      amount: { required, amountValidator: amountValidator() },
      description: { required },
      scheduledPayment: scheduledPaymentFormValidations(),
    },

    transfer: {
      toAccount: { required },
    },

    bankTransfer: {
      accountNumber: {
        required,
        accountNumberValidator: accountNumberValidator(),
      },
      bsb: {
        required,
        bsbValidator: bsbValidator(),
        verifiedValidator: verifiedBsbValidator(),
      },
    },

    bpay: {
      billerCode: {
        required,
        verifiedValidator: verifiedBpayBillerValidator(),
      },
      crn: {
        required,
        minLength: minLength(2),
      },
      verifiedBillerCode: {},
      lastQueriedBillerCode: {},
      verified: { validator: queriedBpayBillerValidator() },
    },

    payId: {
      identifier: {
        required,
        validateIdentifier: payIdIdentifierValidator(),
        verifiedValidator: verifiedPayIdIdentifierValidator(),
      },
      type: {
        required,
      },
      verifiedPayIdIdentifier: {},
      lastQueriedIdentifier: {},
      verified: { validator: queriedPayIdValidator() },
    },
  };

  // Ref<any> is a fix for ts error: "using name 'ChildValidations' from external module "@vuelidate/core/index" but cannot be named."
  // https://github.com/vuelidate/vuelidate/issues/1150
  const validationState: Ref = useVuelidate(validationRules, formState);

  // These record history, not current state - "this field has held an account" - which has to stay
  // true after the field is emptied, so a later emission never fills a field the member cleared.
  // That is why they cannot be computed values: a computed over the field would turn false at the
  // one moment the fact has to persist.
  //
  // The two lines in `seedAccountDefaults` that set a mark are what keep this independent of where
  // the watchers below sit. The seeding watcher is `{ immediate: true }`, so its first run happens
  // as it is registered, before a watcher declared under it exists to see that assignment. Remove
  // them and the order of this file starts deciding whether the rule holds.
  const hasHeldFromAccount = ref(false);
  const hasHeldToAccount = ref(false);

  watch(
    () => formState.details.fromAccount,
    account => {
      if (account) hasHeldFromAccount.value = true;
    },
    { flush: "sync" },
  );

  watch(
    () => formState.transfer.toAccount,
    account => {
      if (account) hasHeldToAccount.value = true;
    },
    { flush: "sync" },
  );

  function seedAccountDefaults() {
    const accounts = accountsForSelect.value;
    if (isEmpty(accounts)) {
      return;
    }
    if (!hasHeldFromAccount.value && !formState.details.fromAccount) {
      formState.details.fromAccount = accounts?.find(account => account.type === AccountType.Transaction) || accounts![0];
      if (formState.details.fromAccount) hasHeldFromAccount.value = true;
    }
    if (!hasHeldToAccount.value && !formState.transfer.toAccount) {
      formState.transfer.toAccount = accounts?.find(account => account.id !== formState.details.fromAccount?.id) ?? null;
      if (formState.transfer.toAccount) hasHeldToAccount.value = true;
    }
  }

  watch(accountsForSelect, seedAccountDefaults, { immediate: true });

  const setPayIdType = (value: PayIdType) => (formState.payId.type = value);

  // Returns true for valid & false for invalid
  async function validateForm() {
    resetValidation();
    let paymentForm: any = validationState.value;

    // noinspection JSUnreachableSwitchBranches
    switch (formState.details.paymentType) {
      case PaymentType.Transfer:
        paymentForm = paymentForm.transfer;
        break;
      case PaymentType.BankTransfer:
        paymentForm = paymentForm.bankTransfer;
        break;
      case PaymentType.BPAY:
        paymentForm = paymentForm.bpay;
        break;
      case PaymentType.PayID:
        paymentForm = paymentForm.payId;
        break;
    }

    try {
      // noinspection ES6MissingAwait
      const validations = [paymentForm.$validate(), validationState.value.details.$validate()];
      return (await Promise.allSettled(validations)).every(e => e.status === "fulfilled" && e.value);
    } catch (e) {
      Sentry.captureException(e);
      error.value = e;
      return false;
    }
  }

  function resetValidation() {
    validationState.value.$reset();
  }

  async function completePayment() {
    const validationResult = await validateForm();
    if (!validationResult) {
      console.log("Form not valid");
      return;
    }

    isSubmitting.value = true;
    error.value = null;

    try {
      const isRegularPayment = formState.details.scheduledPayment!.scheduleType == PaymentScheduleType.Now;

      if (isRegularPayment) {
        await _makeRegularPayment();
        toast.success(t("payment_success"));
      } else {
        await _createScheduledPayment();
        toast.success(t("payments_scheduled_created"));
      }
    } catch (e) {
      Sentry.captureException(e);
      toast.error(t("payment_failure"));
      error.value = e;
      return; // Don't save payee to address book when payment failed
    } finally {
      isSubmitting.value = false;
      formState.details.state = PAYMENT_STATE.CONFIRMED;
    }

    // Split from making a payment, as if saving to the address book fails we don't want to indicate the payment failed
    try {
      if (formState.details.savePayeeToAddressBook) {
        await savePayeeToAddressBook();
      }
    } catch (e) {
      Sentry.captureException(e);
      toast.error("Error saving payee to address book");
      error.value = e;
    } finally {
      isSubmitting.value = false;
    }
  }

  async function _makeRegularPayment() {
    let result: { dateTime: Date | null } | null = null;
    switch (formState.details.paymentType) {
      case PaymentType.Transfer:
        result = await makeTransfer({
          fromAccountId: formState.details.fromAccount!.id,
          toAccountId: formState.transfer.toAccount!.id,
          amount: formState.details.amount!,
          description: formState.details.description,
          toAccountIsLoan: formState.transfer.toAccount!.type === AccountType.Loan,
        });
        break;
      case PaymentType.BankTransfer:
        result = await makeBankTransferPayment({
          name: formState.details.name,
          fromAccountId: formState.details.fromAccount!.id,
          amount: formState.details.amount!,
          description: formState.details.description,

          accountNumber: formState.bankTransfer.accountNumber,
          bsb: formState.bankTransfer.bsb,
        });
        break;
      case PaymentType.PayID:
        result = await makePayIdPayment({
          name: formState.details.name,
          fromAccountId: formState.details.fromAccount!.id,
          amount: formState.details.amount!,
          description: formState.details.description,

          payId: formState.payId.identifier,
        });
        break;
      case PaymentType.BPAY:
        result = await makeBPayPayment({
          name: formState.details.name,
          fromAccountId: formState.details.fromAccount!.id,
          amount: formState.details.amount!,
          description: formState.details.description,

          billerCode: formState.bpay.billerCode,
          reference: formState.bpay.crn,
        });
        break;
    }
    formState.dateTime = result?.dateTime;
  }

  async function _createScheduledPayment() {
    let recipient: Recipient;

    switch (formState.details.paymentType) {
      case PaymentType.BankTransfer:
        recipient = {
          type: RecipientType.AccountRecipient,
          accountNumber: formState.bankTransfer!.accountNumber!,
          bsb: formState.bankTransfer!.bsb!,
        };
        break;
      case PaymentType.PayID:
        recipient = {
          type: RecipientType.PayIdRecipient,
          payId: formState.payId.identifier!,
        };
        break;
      case PaymentType.BPAY:
        recipient = {
          type: RecipientType.BpayRecipient,
          billerCode: formState.bpay.billerCode,
          bpayCrn: formState.bpay.crn,
        };
        break;
      case PaymentType.Transfer:
        throw new Error("Unsupported payment type for scheduled payment");
    }

    let scheduleOptions: ScheduleOptions;

    if (formState.details.scheduledPayment!.scheduleType === PaymentScheduleType.Now) {
      const error = new Error("Could not create payment");

      Sentry.captureException(error, {
        data: {
          message: "Tried to create a scheduled payment with a PaymentScheduleType.now. This should've been a normal payment. ",
        },
      });

      throw error;
    }

    if (formState.details.scheduledPayment!.scheduleType === PaymentScheduleType.Later) {
      scheduleOptions = {
        startDate: formState.details.scheduledPayment!.startDate!,
        frequency: PaymentFrequency.OneOff,
        endCondition: PaymentEndCondition.AfterCount,
        maxCount: 1,
      };
    }

    if (formState.details.scheduledPayment!.scheduleType === PaymentScheduleType.Recurring) {
      switch (formState.details.scheduledPayment!.endType) {
        case PaymentEndCondition.AfterCount:
          scheduleOptions = {
            startDate: formState.details.scheduledPayment!.startDate!,
            frequency: formState.details.scheduledPayment!.frequency!,
            endCondition: PaymentEndCondition.AfterCount,
            maxCount: formState.details.scheduledPayment!.endCount!,
          };
          break;
        case PaymentEndCondition.OnDate:
          scheduleOptions = {
            startDate: formState.details.scheduledPayment!.startDate!,
            frequency: formState.details.scheduledPayment!.frequency!,
            endCondition: PaymentEndCondition.OnDate,
            endDate: formState.details.scheduledPayment!.endDate!,
          };
          break;
        case PaymentEndCondition.Never:
          scheduleOptions = {
            startDate: formState.details.scheduledPayment!.startDate!,
            frequency: formState.details.scheduledPayment!.frequency!,
            endCondition: PaymentEndCondition.Never,
          };
          break;
      }
    }

    await createScheduledPayment({
      name: formState.details.name,
      fromAccountId: formState.details.fromAccount!.id!,
      amount: formState.details.amount!,
      description: formState.details.description,
      recipient,
      scheduleOptions: scheduleOptions!,
    });
  }

  async function savePayeeToAddressBook() {
    try {
      // noinspection JSUnreachableSwitchBranches
      switch (formState.details.paymentType) {
        case PaymentType.BankTransfer:
          await createAccountPayee({
            name: formState.details.name,
            accountNumber: formState.bankTransfer.accountNumber,
            bsb: formState.bankTransfer.bsb,
          });
          break;
        case PaymentType.BPAY:
          await createBillerPayee({
            name: formState.details.name,
            billerCode: formState.bpay.billerCode,
            crn: formState.bpay.crn,
          });
          break;
        case PaymentType.PayID:
          await createPayIdPayee({
            name: formState.details.name,
            payId: formState.payId.identifier,
            payIdType: formState.payId.type!,
          });
          break;
      }

      toast.success(t("payment_payeeSaveSuccess"));
    } catch (e) {
      Sentry.captureException(e);
      toast.error(t("payment_payeeSaveFailure"));
    }
  }

  /**
   * Sets the current payee details.
   *
   * @param payee The payee to pay.
   */
  function setPayee(payee: Record<string, any>) {
    resetValidation();

    setPaymentTypeForPayee(payee);

    formState.details.name = payee.name;
    formState.bankTransfer = {
      accountNumber: payee.accountNumber ?? "",
      bsb: payee.bsb ?? "",
    };
    formState.bpay = {
      billerCode: payee.billerCode ?? "",
      crn: payee.crn ?? "",
      verifiedBillerCode: payee.billerCode ?? "",
      lastQueriedBillerCode: payee.billerCode ?? "",
    };
    formState.payId = {
      identifier: payee.identifier ?? "",
      type: payee.payIdType ?? "",
      verifiedPayIdIdentifier: payee.identifier ?? "",
      lastQueriedIdentifier: payee.identifier ?? "",
    };
    switch (formState.details.paymentType) {
      case PaymentType.BankTransfer:
        formState.bankTransfer.addressBookId = payee.id;
        break;
      case PaymentType.BPAY:
        formState.bpay.addressBookId = payee.id;
        break;
      case PaymentType.PayID:
        formState.payId.addressBookId = payee.id;
        break;
    }
  }

  function clearPayee() {
    setPayee({});
  }

  async function verifyPayId() {
    const isValid = await validationState.value.payId.identifier.$validate();
    if (!isValid) {
      return;
    }

    let payIdDetails;
    formState.details.name = "";

    const { resolveClient } = useApolloClient();
    const client = resolveClient();
    try {
      const result = await client.query({
        query: LookupPayIdDocument,
        variables: {
          payId: formState.payId.identifier,
          type: formState.payId.type!,
        },
      });

      if (result.data?.lookupPayId) {
        payIdDetails = useFragment(PayIdDetailsFragmentFragmentDoc, result?.data?.lookupPayId);
        formState.details.name = payIdDetails?.name ?? payIdDetails?.accountDetails?.name;
        formState.payId.verifiedPayIdIdentifier = formState.payId.identifier;
      }
    } catch (err) {
      console.warn("Error verifying PayID", err);
      // ignore the error
    } finally {
      formState.payId.lastQueriedIdentifier = formState.payId.identifier;
    }
  }

  async function verifyBpayBiller() {
    const isValid = await validationState.value.bpay.billerCode.$validate();
    if (!isValid) {
      return;
    }

    let billerDetails;
    formState.details.name = "";

    const { resolveClient } = useApolloClient();
    const client = resolveClient();
    try {
      const result = await client.query({
        query: LookupBpayBillerDocument,
        variables: {
          billerCode: formState.bpay.billerCode,
        },
      });

      if (result.data?.lookupBpayBiller) {
        billerDetails = useFragment(BPayBillerFragmentFragmentDoc, result?.data?.lookupBpayBiller);
        formState.details.name = billerDetails?.billerShortName;
        formState.bpay.verifiedBillerCode = formState.bpay.billerCode;
      }
    } catch (err) {
      console.warn("Error verifying BPAY Biller", err);
      // ignore the error
    } finally {
      formState.bpay.lastQueriedBillerCode = formState.bpay.billerCode;
    }
  }

  function setPaymentTypeForPayee(payee: { __typename?: string }) {
    // update page payment type
    let newPaymentType: PaymentType = formState.details.paymentType;
    switch (payee.__typename) {
      case PayeeType.accountPayee:
        newPaymentType = PaymentType.BankTransfer;
        break;
      case PayeeType.billerPayee:
        newPaymentType = PaymentType.BPAY;
        break;
      case PayeeType.payIdPayee:
        newPaymentType = PaymentType.PayID;
        break;
    }
    formState.details.paymentType = newPaymentType;
  }

  function setPaymentType(paymentType: PaymentType) {
    // Always reset if we are on the confirm or payment made screens
    if (formState.details.state !== PAYMENT_STATE.NEW) {
      $reset({ paymentType });
    }
    formState.details.paymentType = paymentType;
  }

  function onPaymentTypeChanged(paymentType: PaymentType) {
    setPaymentType(paymentType);
    switch (paymentType) {
      case PaymentType.Transfer:
        return router.replace({ name: routeNames.TRANSFER_VIEW });
      default:
        router.replace({ name: routeNames.PAYMENT_VIEW, query: { type: paymentType } });
    }
  }

  const isVerifiedBpayBiller = computed(() => {
    const bpay = formState.bpay;
    return bpay?.billerCode === bpay?.verifiedBillerCode && bpay?.verifiedBillerCode?.length > 0 && formState.details?.name.length > 0;
  });

  const isVerifiedPayId = computed(() => {
    const payId = formState.payId;
    return (
      payId?.identifier === payId?.verifiedPayIdIdentifier &&
      payId?.verifiedPayIdIdentifier?.length > 0 &&
      formState.details?.name.length > 0
    );
  });

  const payeeIsFromAddressBook = computed(() => {
    switch (formState.details.paymentType) {
      case PaymentType.BankTransfer:
        return !!formState.bankTransfer.addressBookId;
      case PaymentType.BPAY:
        return !!formState.bpay.addressBookId;
      case PaymentType.PayID:
        return !!formState.payId.addressBookId;
    }
    return false;
  });

  async function handleBack() {
    if (formState.details.state === PAYMENT_STATE.VERIFY) {
      formState.details.state = PAYMENT_STATE.NEW;
    } else if (formState.details.state === PAYMENT_STATE.CONFIRMED) {
      await refetchAccounts(); // Refresh accounts so the balance updates
      $reset({ paymentType: formState.details.paymentType });
    }
  }

  async function handleNext() {
    if (formState.details.state === PAYMENT_STATE.NEW) {
      const validationResult = await validateForm();
      if (!validationResult) {
        console.log("Form not valid");
        return;
      }

      formState.details.state = PAYMENT_STATE.VERIFY;
    } else if (formState.details.state === PAYMENT_STATE.VERIFY) {
      await completePayment();
    } else if (formState.details.state === PAYMENT_STATE.CONFIRMED) {
      await router.push({
        name: routeNames.ACCOUNT_DETAILS_VIEW,
        params: { accountId: formState.details.fromAccount?.id },
      });
      return;
    }
  }

  function $reset({ paymentType }: { paymentType?: PaymentType } = {}) {
    Object.assign(formState, generateInitialStoreForm());
    validationState.value.$reset();
    error.value = null;
    if (paymentType) formState.details.paymentType = paymentType;

    hasHeldFromAccount.value = false;
    hasHeldToAccount.value = false;
    seedAccountDefaults();
  }

  return {
    error: readonly(error),
    formState,
    validationState: readonly(validationState),
    fromAccountName: readonly(fromAccountName),
    isSubmitting: readonly(isSubmitting),
    loadingAccounts: readonly(loadingAccounts),
    payeeIsFromAddressBook,

    setPayIdType,
    setPayee,
    clearPayee,
    setPaymentType,
    verifyPayId,
    verifyBpayBiller,
    handleBack,
    handleNext,
    onPaymentTypeChanged,
    accountsForSelect,
    isVerifiedBpayBiller,
    isVerifiedPayId,
    resetPaymentForm: $reset,
  };
});
